import { useCallback, useEffect, useState } from 'react';
import {
  SYSTEM_DARK_QUERY,
  VISITOR_PALETTES,
  readStoredVisitorTheme,
  resolveVisitorTheme,
  visitorThemeCssVariables,
  visitorThemeStorage,
  writeStoredVisitorTheme,
} from './visitorTheme';

// A local copy of src/admin/useMediaQuery.js rather than an import: nothing under `src/admin/`
// may be reachable from the visitor bundle (the lazy-load boundary, #310), and importing across
// that line would pull the dashboard chunk into every visitor page load.
//
// Seeded synchronously rather than in the effect: starting at `false` means a system-dark device
// paints the light palette once and flips after paint — a full-screen white flash on every load
// (cold review, #416).
function readMatch(query) {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  } catch (error) {
    return false;
  }
}

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => readMatch(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let media;
    try {
      media = window.matchMedia(query);
    } catch (error) {
      return undefined;
    }
    const onChange = event => setMatches(event.matches);
    setMatches(media.matches);
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, [query]);

  return matches;
}

// The document canvas — the scrollbars and the overscroll band behind the app — is painted from
// the ROOT element's own scheme and background, not from an inner div's, so the resolved mode is
// written there and put back exactly as it was on unmount.
function useDocumentCanvas(mode) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const canvas = (VISITOR_PALETTES[mode] || VISITOR_PALETTES.light).canvas;
    const previousBackground = root.style.backgroundColor;
    const previousScheme = root.style.colorScheme;
    const previousCanvasVar = root.style.getPropertyValue('--visitor-canvas');
    root.style.backgroundColor = canvas;
    root.style.colorScheme = mode;
    // Also published as the variable, because src/index.css paints `<body>` with
    // `var(--visitor-canvas, …)` and a variable set on the inner app wrapper does not inherit
    // UPWARD. Without this, a dark app on a viewport wider than the 448px shell keeps light
    // gutters either side of it (cold review, #422).
    root.style.setProperty('--visitor-canvas', canvas);
    return () => {
      root.style.backgroundColor = previousBackground;
      root.style.colorScheme = previousScheme;
      if (previousCanvasVar) root.style.setProperty('--visitor-canvas', previousCanvasVar);
      else root.style.removeProperty('--visitor-canvas');
    };
  }, [mode]);
}

// The browser tints the status bar / address bar from the FIRST `<meta name="theme-color">`
// whose `media` matches. public/index.html carries a light/dark pair qualified by
// `prefers-color-scheme`, which is right for "System" and is what paints before any script runs
// — but it cannot answer an explicit override: a visitor who picks Dark on a light device would
// keep a cream status bar over a dark app. While an override is stored, an UNQUALIFIED meta is
// inserted ahead of the pair (an absent `media` always matches, and document order decides), and
// removed again the moment the choice returns to System.
const THEME_COLOR_OVERRIDE_SELECTOR = 'meta[name="theme-color"][data-visitor-theme-override]';

function useThemeColorMeta(mode, stored) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const head = document.head;
    if (!head) return undefined;
    const remove = () => {
      const stale = head.querySelector(THEME_COLOR_OVERRIDE_SELECTOR);
      if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
    };
    if (stored !== 'light' && stored !== 'dark') {
      // Back to System: the device pair is right again, so nothing may sit in front of it.
      remove();
      return undefined;
    }
    // ADOPT the node the seed in public/index.html already inserted, rather than adding a second
    // one in front of it — two overrides would leave the first reload's colour winning forever.
    const meta = head.querySelector(THEME_COLOR_OVERRIDE_SELECTOR) || document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    meta.setAttribute('data-visitor-theme-override', '');
    meta.setAttribute('content', (VISITOR_PALETTES[mode] || VISITOR_PALETTES.light).paper);
    if (!meta.parentNode) {
      const anchor = head.querySelector('meta[name="theme-color"]');
      if (anchor) head.insertBefore(meta, anchor);
      else head.appendChild(meta);
    }
    return remove;
  }, [mode, stored]);
}

// Written and unit-tested in #422, MOUNTED by src/App.js in #423: the resolved mode now drives
// every visitor surface except the Map tab, which #423 deliberately pins to the light palette
// until #424 gives it a dark `styles` array and dark marker tiers.
export function useVisitorTheme() {
  const systemDark = useMediaQuery(SYSTEM_DARK_QUERY);
  // `visitorThemeStorage()` and not `window.localStorage` — see the comment on it in
  // visitorTheme.js.
  const [stored, setStored] = useState(() => readStoredVisitorTheme(visitorThemeStorage()));
  const mode = resolveVisitorTheme({ stored, systemDark });
  const setMode = useCallback(next => {
    writeStoredVisitorTheme(visitorThemeStorage(), next);
    setStored(next);
  }, []);
  useDocumentCanvas(mode);
  useThemeColorMeta(mode, stored);
  return {
    mode,
    stored,
    setMode,
    rootStyle: { ...visitorThemeCssVariables(mode), colorScheme: mode },
  };
}
